<template>
    <div class="form-group" :class="{'has-error': errors.has('activity') }">
        <label class="col-xs-12 col-sm-2 control-label">Activity:</label>
        <div class="col-xs-12 col-sm-10">
            <div class="row activity-picker">
                <!-- Activity dropdown -->
                <div :class="['dropdown-container col-xs-12 col-sm-6', teEntryOption === TE_ENTRY_OPTIONS.BUILDTIME ? 'col-md-4' : '']">
                    <custom-select 
                        ref="vselect"
                        :options="activityOptions"
                        :on-change="onActivitySelectionChange"
                        :clearSearchOnSelect="true"
                        :value="selectedActivity"
                        :loading="loading"
                        :disabled="$store.getters.restrictedEditMode || $store.state.timeEntry.isAuditEdit"
                        :selectOnTab="true" />
                </div>

                <!-- Wrapper for Target and Role - forces them to wrap together -->
                <div v-if="teEntryOption === TE_ENTRY_OPTIONS.BUILDTIME && selectedActivity && showTargets"
                     class="col-xs-12 col-sm-12 col-md-8">
                    <div class="row">
                        <!-- Target dropdown -->
                        <div class="col-xs-12 col-sm-6">
                            <div class="row">
                        <label class="col-xs-12 col-sm-3 control-label">{{ targetDropdownLabel }}:</label>
                        <div class="col-xs-12 col-sm-9">
                            <custom-select 
                                ref="vtarget"
                                :options="targetOptions"
                                :on-change="onTargetSelectionChange"
                                :clearSearchOnSelect="true"
                                :value="selectedTarget"
                                :loading="loadingTargets"
                                :disabled="$store.getters.restrictedEditMode || $store.state.timeEntry.isAuditEdit"
                                :selectOnTab="true" />
                        </div>
                            </div>
                        </div>

                        <!-- Role dropdown -->
                        <div v-if="selectedTarget && showRoles"
                             class="col-xs-12 col-sm-6">
                            <div class="row">
                        <label class="col-xs-12 col-sm-3 control-label">Who:</label>
                        <div class="col-xs-12 col-sm-9">
                            <custom-select 
                                ref="vrole"
                                :options="roleOptions"
                                :on-change="onRoleSelectionChange"
                                :clearSearchOnSelect="true"
                                :value="selectedRole"
                                :loading="loadingRoles"
                                :disabled="$store.getters.restrictedEditMode || $store.state.timeEntry.isAuditEdit"
                                :selectOnTab="true" />
                        </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Code line: full width under all dropdowns -->
                <div v-if="selectedCode" class="col-xs-12">
                    <div class="selected-code">
                        <small class="text-muted">Code: {{ selectedCode }}</small>
                    </div>
                </div>
            </div>
            <p class="text-danger" v-if="errors.has('activity') || activityError">
                {{ activityError || errors.first('activity') }}
            </p>
        </div>
    </div>
</template>

<script>
    import Vue from "vue";
    import vSelect from 'vue-select';
    import axios from 'axios';
    import _ from "lodash";
    import { TE_ENTRY_OPTIONS } from '../../_shared/constants';

    Vue.component('v-select', vSelect)

    Vue.component('custom-select', {
        extends: vSelect,
        methods: {
            maybeAdjustScroll: () => false
        },
        computed: {
            filteredOptions() {
                if (!this.search.length) {
                    return this.mutableOptions;
                }

                let search = this.search.toLowerCase();

                let codeMatches = _.filter(this.mutableOptions, function (o) {
                    return o.label.toLowerCase().indexOf(search) === 0;
                });

                let otherMatches = _.filter(this.mutableOptions, function (o) {
                    return o.label.toLowerCase().indexOf(search) > 0;
                })

                return codeMatches.concat(otherMatches);
            }
        }
    })

    export default {
        data: function () {
            return {
                selectedActivity: null,
                selectedTarget: null,
                selectedRole: null,
                initialActivity: "",
                initialTarget: "",
                initialRole: "",
                activityOptions: [],
                targetOptions: [],
                roleOptions: [],
                loading: false,
                loadingTargets: false,
                loadingRoles: false,
                showTargets: false,
                showRoles: false,
                selectedCode: null,
                targetDropdownLabel: null
            };
        },
        components: { vSelect },
        props: {
            value: String,
            username: String,
            isMarketing: Boolean,
            isOutlook: Boolean,
            teEntryOption: {
                type: String,
                default: TE_ENTRY_OPTIONS.BUILDTIME
            },
            activityError: String
        },
        watch: {
            // watch username change to fetch user activity codes
            username: function (newVal, oldVal) {
                this.clearSelection();
                this.refreshActivities();
            },
            // watch teEntryOption changes
            teEntryOption: function (newVal, oldVal) {
                this.resetSelections();
                this.refreshActivities();
            }
        },
        methods: {
            refreshActivities() {
                let url = "/core/api";
                // Build base URL with teEntryOption in the path
                if (this.isMarketing) {
                    url += "/activitycodes/marketing";
                } else {
                    if (this.isOutlook) {
                        url = this.$store.state.keaUrl + url + "/activitycodes/outlook/" + this.$store.state.isSentItems + "/user/";
                    } else {
                        url += "/activitycodes/claims/user/";
                    }
                    if (this.username) {
                        url = url + this.username;
                    }
                    url += "/" + this.teEntryOption;
                }

                if (this.teEntryOption === TE_ENTRY_OPTIONS.BUILDTIME && !this.isMarketing) {
                    this.getActions(url);
                } else {
                    this.getActivityCodes(url);
                }
            },

            // Activity dropdown change handler
            onActivitySelectionChange(item) {
                this.selectedActivity = item;

                if (this.teEntryOption === TE_ENTRY_OPTIONS.BUILDTIME && !this.isMarketing) {
                    // For option 'BuildTime', get targets for the selected action
                    this.selectedTarget = null;
                    this.selectedRole = null;
                    this.showTargets = false;
                    this.showRoles = false;
                    this.selectedCode = null;
                    this.$emit('input', '');
                    if (item && item.value) {
                        if (item.hasTargets) {
                            this.targetDropdownLabel = item.targetDropdownLabel;
                            this.getTargets(item.value);
                        }
                        else {
                            this.selectedCode = item.code + ' - ' + item.description;
                            this.$emit('input', item ? item.code : '');
                        }
                    } else {
                        this.showTargets = false;
                    }
                } else {
                    // For options 'SelectTime' and 'Expense', directly emit the selected activity
                    this.$emit('input', item ? item.value : '');
                }
            },

            onTargetSelectionChange(item) {
                this.selectedTarget = item;
                this.selectedRole = null;
                this.selectedCode = null;
                this.$emit('input', '');

                if (item && item.value) {

                    if (item.hasRoles) {
                        //     this.$emit('target-selected', item);
                        this.getRoles(item.value);
                    }
                    else {
                        this.selectedCode = item.code + ' - ' + item.description;
                        this.$emit('input', item ? item.code : '');
                    }
                }
                else {
                    this.showRoles = false;
                }
            },

            onRoleSelectionChange(item) {
                this.selectedRole = item;
                if (item && item.value) {
                    this.selectedCode = item.code + ' - ' + item.description;
                    this.$emit('input', item ? item.code : '');
                }
                else {
                    this.$emit('input', '');
                }
            },

            getActivityCodes(url) {
                this.loading = true;
                let authHeader = this.getAuthHeader();

                axios.get(url, authHeader)
                    .then(response => {
                        this.activityOptions = [];
                        var self = this;
                        response.data.forEach(function (ac) {
                            var option = {
                                label: ac.code + " - " + ac.description,
                                value: ac.code
                            };
                            self.activityOptions.push(option)
                            if (ac.code == self.initialActivity) {
                                self.selectedActivity = option;
                            }
                        });
                        this.loading = false;
                    })
                    .catch(error => {
                        console.error('Error fetching activity codes:', error);
                        this.loading = false;
                    });
            },

            getActions(url) {
                this.loading = true;
                let authHeader = this.getAuthHeader();
                let lineInserted = true;

                axios.get(url, authHeader)
                    .then(response => {
                        this.activityOptions = [];
                        var self = this;
                        response.data.forEach(function (action) {
                            var option = {
                                label: action.name,
                                value: action.name,
                                hasTargets: action.hasTargets,
                                code: action.code,
                                description: action.description,
                                targetDropdownLabel: action.targetDropdownLabel
                            };

                            // Add separator after top items only if there were any and not Outlook
                            if (self.isOutlook) {
                                lineInserted = true;
                            }
                            else if (!action.isBottom) {
                                lineInserted = false;
                            }
                            else if (!lineInserted) {
                                const separator = {
                                    label: '───────────────────',
                                    value: '',
                                    disabled: true,
                                    hasTargets: false,
                                    code: '',
                                    description: '',
                                    targetDropdownLabel: ''
                                };
                                self.activityOptions.push(separator);
                                lineInserted = true;
                            }
                            // Now add the entry
                            self.activityOptions.push(option)

                            if (action.name == self.initialActivity) {
                                self.selectedActivity = option;
                            }
                        });
                        this.loading = false;
                    })
                    .catch(error => {
                        console.error('Error fetching actions:', error);
                        this.loading = false;
                    });
            },

            getTargets(action) {
                this.loadingTargets = true;
                let encodedAction = encodeURIComponent(action);
                let url = `/core/api/activitycodes/targets/${encodedAction}`;
                if (this.isOutlook) {
                    url = this.$store.state.keaUrl + url;
                }
                let authHeader = this.getAuthHeader();

                axios.get(url, authHeader)
                    .then(response => {
                        this.targetOptions = [];
                        var self = this;
                        response.data.forEach(function (target) {
                            var option = {
                                label: target.name,
                                value: target.name,
                                hasRoles: target.hasRoles,
                                code: target.code,
                                description: target.description
                            };
                            self.targetOptions.push(option);
                            if (target.name == self.initialTarget) {
                                self.selectedTarget = option;
                            }
                        });
                        this.showTargets = true;
                        this.loadingTargets = false;
                    })
                    .catch(error => {
                        console.error('Error fetching targets:', error);
                        this.loadingTargets = false;
                    });
            },

            getRoles(target) {
                this.loadingRoles = true;
                let encodedTarget = encodeURIComponent(target);
                let url = `/core/api/activitycodes/roles/${encodedTarget}`;
                if (this.isOutlook) {
                    url = this.$store.state.keaUrl + url;
                }
                let authHeader = this.getAuthHeader();

                axios.get(url, authHeader)
                    .then(response => {
                        this.roleOptions = [];
                        var self = this;
                        response.data.forEach(function (role) {
                            var option = {
                                label: role.name,
                                value: role.name,
                                code: role.code,
                                description: role.description
                            };
                            self.roleOptions.push(option);
                            if (role.name == self.initialRole) {
                                self.selectedRole = option;
                            }
                        });
                        this.showRoles = true;
                        this.loadingRoles = false;
                    })
                    .catch(error => {
                        console.error('Error fetching roles:', error);
                        this.loadingRoles = false;
                    });
            },

            getAuthHeader() {
                if (this.isOutlook) {
                    return { headers: { "Authorization": "Bearer " + this.$store.state.accessToken } };
                }
                return {};
            },

            resetSelections() {
                this.selectedActivity = null;
                this.selectedTarget = null;
                this.selectedRole = null;
                this.showTargets = false;
                this.showRoles = false;
                this.targetOptions = [];
                this.roleOptions = [];
                this.selectedCode = null;
                this.$emit('input', null);
            },

            clearSelection() {
                this.resetSelections();
                if (this.$refs.vselect) {
                    this.$refs.vselect.clearSelection();
                }
                if (this.$refs.vtarget) {
                    this.$refs.vtarget.clearSelection();
                }
                if (this.$refs.vrole) {
                    this.$refs.vrole.clearSelection();
                }
                this.refreshActivities();
            },

            setSelection(activityCode, activityCodeAction, activityCodeTarget, activityCodeRole) {
                // This is called when reviving session to restore dropdowns after leaving the page to add/edit contact which is on a different URL
                if (this.teEntryOption !== TE_ENTRY_OPTIONS.BUILDTIME) {
                    if (activityCode) {
                        this.initialActivity = activityCode;
                    }
                }
                else if (activityCodeAction) {
                    this.initialActivity = activityCodeAction;
                    if (activityCodeTarget) {
                        this.initialTarget = activityCodeTarget;
                        if (activityCodeRole) {
                            this.initialRole = activityCodeRole;
                        }
                    }
                }

                var self = this;
                if (this.teEntryOption !== TE_ENTRY_OPTIONS.BUILDTIME) {
                    if (this.initialActivity !== "") {
                        this.activityOptions.forEach(function (option) {
                            if (option.value === self.initialActivity) {
                                self.selectedActivity = option;
                            }
                        });
                    }
                }
                else {
                    if (this.initialActivity !== "") {
                        this.activityOptions.forEach(function (option) {
                            if (option.value === self.initialActivity) {
                                self.selectedActivity = option;
                            }
                        });
                        if (this.initialTarget !== "") {
                            this.targetOptions.forEach(function (option) {
                                if (option.value === self.initialTarget) {
                                    self.selectedTarget = option;
                                }
                            });
                            if (this.initialRole !== "") {
                                this.roleOptions.forEach(function (option) {
                                    if (option.value === self.initialRole) {
                                        self.selectedRole = option;
                                    }
                                });
                            }
                        }
                    }
                }
            }
        },
        // fetches list when the component is created.
        created() {
            this.refreshActivities();
            this.initialActivity = this.value;
        },
        computed: {
            TE_ENTRY_OPTIONS() {
                return TE_ENTRY_OPTIONS;
            }
        }
    };
</script>

<style lang="scss">
    @use "vselect-styles.scss";

    .selected-code {
        margin-top: 2px;
    }

    .activity-picker .w-100 {
        display: block;
        width: 100%;
    }

    /* nicer spacing + left-aligned labels on phones */
    @media (max-width: 767px) {
        .activity-picker .control-label {
            text-align: left;
        }
    }

    /* Only add margin between rows when Target+Role wrap to next line (sm screens) */
    @media (min-width: 768px) and (max-width: 991px) {
        .activity-picker > .dropdown-container {
            margin-bottom: 15px;
        }
    }
</style>